import { Trans } from 'react-i18next';
import { t } from 'i18next';
import { Button } from '@peacecover/component';
import { CheckCircleFill } from '@peacecover/icon';
import { useBatchUpdateTradingPhotoCardsMutation } from '../hooks/useBatchUpdateTradingPhotoCardsMutation';
import { useLoadingStep } from '../hooks/useLoadingStep';
import GradientAttentionIcon from '../../template/edit-collected-photo-cards/icons/GradientAttentionIcon';
import LoadingActionsBar from './LoadingActionsBar';
import { useSelectedTradingCardsStore } from '../store/useSelectedTradingCardsStore';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';

interface TradingPhotoCardActionsBarProps {
  totalCount?: number;
  onToggleSelectAll?: () => void;
  activeTab: number;
}

const TradingPhotoCardActionsBar = ({
  totalCount = 0,
  onToggleSelectAll,
  activeTab
}: TradingPhotoCardActionsBarProps) => {
  const { mutate: batchUpdateTradingPhotoCards, isLoading } =
    useBatchUpdateTradingPhotoCardsMutation();

  const { selectedPhotoCards, clearPhotoCards } = useSelectedTradingCardsStore();
  const statusStep = useLoadingStep(isLoading);

  const hasSelection = selectedPhotoCards.length > 0;
  const isAllSelected = hasSelection && selectedPhotoCards.length === totalCount;

  const handleChangeToCollected = async () => {
    if (activeTab === 1) {
      openNewTab('/new-my-trading-photo-cards/register-sale');
      return;
    }
    try {
      const tradingPhotoCards = selectedPhotoCards.map((photoCard) => ({
        ...photoCard,
        tradingPhotoCardId: photoCard.tradingPhotoCardId,
        category: 'exchange'
      }));
      batchUpdateTradingPhotoCards(tradingPhotoCards);
    } catch (error) {
      console.error('Error changing to collected:', error);
    } finally {
      clearPhotoCards();
    }
  };

  const handleChangeToSold = () => {
    try {
      const tradingPhotoCards = selectedPhotoCards.map((photoCard) => ({
        ...photoCard,
        tradingPhotoCardId: photoCard.tradingPhotoCardId,
        status: 'sold_out'
      }));
      batchUpdateTradingPhotoCards(tradingPhotoCards);
    } catch (error) {
      console.error('Error changing to sold:', error);
    } finally {
      clearPhotoCards();
    }
  };

  if (statusStep !== 3) {
    // 업로드 중일 때 로딩 바 표시
    return (
      <LoadingActionsBar
        step={statusStep}
        statusCards={statusCards}
        className="mx-auto max-w-[var(--chakra-sizes-xl)]"
      />
    );
  }

  return (
    <div
      className="mx-auto flex max-w-[var(--chakra-sizes-xl)] flex-col items-center justify-center gap-[10px] fixed bottom-0 left-0 right-0 bg-pure-white dark:bg-pure-black pt-[14px] z-10 rounded-t-[20px] px-[24px] shadow-[0_-1px_10px_0_rgba(0,0,0,0.03),_0_-1px_3px_0_rgba(0,0,0,0.08)] dark:shadow-[0_-1px_3px_0_rgba(0,0,0,0.20),_0_-1px_10px_0_rgba(0,0,0,0.24)]"
      style={{
        paddingBottom: 'calc(10px + env(safe-area-inset-bottom))'
      }}
    >
      {!hasSelection ? (
        <div className="flex items-center gap-[4px] h-[28px]">
          <GradientAttentionIcon width={22} height={22} className="my-[3px]" />
          <p className="flex items-center bg-gradient-single-red bg-clip-text text-transparent text-[15px] h-[18px] my-[5px] font-bold leading-none">
            {t('photoCardPage.changeToCollectedAlert')}
          </p>
        </div>
      ) : (
        <div className="flex items-center justify-between h-[28px] w-full">
          <p className="text-[15px] text-text-main-lv2">
            <Trans
              i18nKey="NewTradingPhotoCards.selectedPhotoCardCount"
              values={{ count: selectedPhotoCards.length }}
              components={{ strong: <strong /> }}
            />
          </p>
          <div className="flex items-center gap-[4px] leading-none">
            <button type="button" onClick={onToggleSelectAll}>
              <CheckCircleFill
                size={24}
                backgroundColor={
                  isAllSelected ? 'var(--color-primary)' : 'var(--color-surface-lv2)'
                }
                strokeColor={isAllSelected ? 'var(--color-on-primary)' : 'var(--color-on-disable)'}
              />
            </button>
            <span className="text-[14px] font-medium text-text-main-lv2">
              {t('NewTradingPhotoCards.selectAll')}
            </span>
          </div>
        </div>
      )}

      <div className="w-full flex gap-[8px]">
        <Button
          btnColor="secondary"
          size="large"
          className="text-[17px] font-semibold w-full"
          disabled={!hasSelection || isLoading}
          onClick={handleChangeToCollected}
        >
          {activeTab === 0
            ? t('NewTradingPhotoCards.changeToCollectedAlert')
            : t('NewTradingPhotoCards.changeToSaleAlert')}
        </Button>
        <Button
          btnColor="warning"
          size="large"
          className="text-[17px] font-semibold w-full bg-[var(--color-warning)] disabled:bg-disable"
          disabled={!hasSelection || isLoading}
          onClick={handleChangeToSold}
        >
          {t('NewTradingPhotoCards.changeToSoldAlert')}
        </Button>
      </div>
    </div>
  );
};

export default TradingPhotoCardActionsBar;


const statusCards = [
  {
    icon: '3D.loading',
    title: t('NewTradingPhotoCards.loadingActionsBarTitleInProgress', { dots: '...' }),
    subtitle: t('NewTradingPhotoCards.loadingActionsBarSubtitleInProgress'),
    iconClassName: 'w-[32px] h-[32px] mt-[12px]'
  },
  {
    icon: '3D.Hammer and wrench',
    title: t('NewTradingPhotoCards.loadingActionsBarTitleDelayed'),
    subtitle: t('NewTradingPhotoCards.loadingActionsBarSubtitleDelayed'),
    iconClassName: 'w-[32px] h-[32px] mt-[12px]'
  },
  {
    icon: '3D.checkmark',
    title: t('NewTradingPhotoCards.loadingActionsBarTitleCompleted'),
    subtitle: '',
    iconClassName: 'w-[34px] h-[34px] mt-[12px]'
  }
];